import { FC, useMemo } from 'react';
import { Column, FuzzySearchSelect } from '@/components/UI/FuzzySearchSelect/FuzzySearchSelect';
import { _notNil } from '@/littledash';
import { RiGitForkFill, RiSquareFill } from 'react-icons/ri';
import { PlannedSampleOption, useAssignAnimalsContext } from '@/components/Samples/AssignAnimals/AssignAnimalsProvider';
import { useAddSamplePlanContext } from '@/components/Samples/AddSamplePlanProvider';
import styles from '@/components/Samples/AssignAnimals/AssignAnimalsForm.module.scss';
import { GroupTarget, PlannedAnimalTarget } from '@/model/SamplePlan.model';
import { aggregateSamplesForAnimal } from '@/components/Samples/AssignAnimals/AssignAnimal.util';

type ItemData = PlannedAnimalTarget & {
  plannedSamples: Array<PlannedSampleOption>;
  study_group?: GroupTarget;
};
const searchIndex = [
  { name: 'planned_id' },
  { name: ['study_group', 'name'] },
  { name: ['plannedSamples', 'name'] },
  { name: ['plannedSamples', 'sample_name'] },
];

export const PlannedAnimalSearchSelect: FC = () => {
  const { groups } = useAddSamplePlanContext();
  const {
    selectedPlannedAnimalTargets,
    updateSelection,
    targetToPlannedSamples,
    plannedSampleOptions,
    plannedAnimalsOptions,
  } = useAssignAnimalsContext();

  const items = useMemo<Map<PlannedAnimalTarget['api_id'], ItemData>>(() => {
    return new Map(
      Array.from(plannedAnimalsOptions.values())
        .map<[PlannedAnimalTarget['api_id'], ItemData]>((plannedAnimalTarget) => {
          const plannedSamples = aggregateSamplesForAnimal(
            [plannedAnimalTarget.study_group_api_id, plannedAnimalTarget.api_id, plannedAnimalTarget.animal_api_id],
            targetToPlannedSamples,
            plannedSampleOptions
          );
          return [
            plannedAnimalTarget.api_id,
            {
              ...plannedAnimalTarget,
              plannedSamples,
              study_group: _notNil(plannedAnimalTarget.study_group_api_id)
                ? groups.get(plannedAnimalTarget.study_group_api_id)
                : undefined,
            },
          ];
        })
        .sort((left, right) =>
          (left?.[1]?.planned_id ?? '').localeCompare(right?.[1]?.planned_id ?? '', undefined, { numeric: true })
        )
    );
  }, [plannedAnimalsOptions, targetToPlannedSamples, plannedSampleOptions, groups]);

  const columns = useMemo<Array<Column<ItemData>>>(
    () => [
      {
        id: 'name',
        title: 'Animal ID',
        cell: (data) => (
          <div className="flex flex-row items-center justify-between w-100">
            <span>{data.planned_id}</span>
            {_notNil(data.animal_api_id) && <RiGitForkFill className="gray" style={{ rotate: '-90deg' }} />}
          </div>
        ),
      },
      {
        id: 'group',
        title: 'Group',
        cell: (data) =>
          _notNil(data.study_group) ? (
            <div className="flex flex-row items-center gap-h-1">
              <RiSquareFill color={data.study_group.color} />
              <span>{data.study_group.name}</span>
            </div>
          ) : (
            '-'
          ),
      },
      {
        id: 'planned_sample_group',
        title: 'Name',
        cell: (data) => (
          <div className={styles.Chips}>
            {data.plannedSamples.map((sample) => (
              <div className={styles.Chip} key={sample.api_id}>
                {sample.name}
              </div>
            ))}
          </div>
        ),
      },
    ],
    []
  );

  const selected = useMemo(() => {
    const result = new Map<PlannedAnimalTarget['api_id'], boolean>();
    selectedPlannedAnimalTargets.forEach((key) => {
      result.set(key, true);
    });
    return result;
  }, [selectedPlannedAnimalTargets]);

  return (
    <FuzzySearchSelect
      items={items}
      columns={columns}
      searchIndex={searchIndex}
      selected={selected}
      onSelectionChange={(value) => updateSelection({ type: 'target', targetType: 'planned_animals', value })}
    />
  );
};
